import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { tabContentTransitionAnimation } from '../task-animations';
import { MentionComponent } from '@syncfusion/ej2-angular-dropdowns';

/**
 * Interface for milestone data
 */
export interface TaskMilestone {
  id: string | number;
  name: string;
  status: 'upcoming' | 'in-progress' | 'completed' | 'overdue';
  dueDate?: Date | string;
  description?: string;
  startDate?: Date | string;
  endDate?: Date | string;
  progress?: number;
  taskStats?: {
    total: number;
    completed: number;
  };
  daysRemaining?: number;
  assignedTo?: string;
}

/**
 * Interface for tab change events
 */
export interface TaskTabChangeEvent {
  previousIndex: number;
  currentIndex: number;
}

/**
 * TaskMainContentComponent
 *
 * Right panel container for main task content with:
 * - Large task name input (2xl font, border-bottom on focus)
 * - Description section with edit button
 * - Optional milestone card with colored status indicator
 * - Material tab navigation with 5 tabs and badges
 * - Tab content projection with animations
 *
 * @selector app-task-main-content
 * @changeDetection OnPush
 */
@Component({
    selector: 'app-task-main-content',
    templateUrl: './task-main-content.component.html',
    styleUrls: ['./task-main-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [tabContentTransitionAnimation],
    standalone: false
})
export class TaskMainContentComponent implements OnInit, OnChanges {
  /**
   * Task form group containing all form controls
   */
  @Input() taskForm!: FormGroup;

  /**
   * Selected milestone (optional)
   */
  @Input() selectedMilestone?: TaskMilestone;

  /**
   * Checklist items count
   */
  @Input() checklistCount: number = 0;

  /**
   * Attachments count
   */
  @Input() attachmentsCount: number = 0;

  /**
   * Comments count
   */
  @Input() commentsCount: number = 0;

  /**
   * Active tab index (0-based)
   */
  @Input() activeTab: number = 0;

  /**
   * Module & Reference Selection Inputs
   */
  @Input() isTaskModule: boolean = false;
  @Input() modulesList: any[] = [];
  @Input() searchParams: any = { type: '', module_id: '', search: '' };
  @Input() selectedItem: any = null;
  @Input() showSearchInput: boolean = true;

  /**
   * Mentions array for @mention functionality
   */
  @Input() set mentions(value: any[]) {
    this._mentions = value || [];
    console.log('[CUSTOM MENTION] Mentions updated:', this._mentions.length, 'users');

    // Force change detection for OnPush strategy
    this.cdr.markForCheck();
  }
  get mentions(): any[] {
    return this._mentions;
  }
  private _mentions: any[] = [];

  /**
   * Loading state for mentions dropdown
   */
  @Input() isMentionsLoading: boolean = false;

  /**
   * Content flags for placeholders
   */
  @Input() hasDetailsContent: boolean = false;
  @Input() hasChecklistContent: boolean = false;
  @Input() hasAttachmentsContent: boolean = false;
  @Input() hasCommentsContent: boolean = false;
  @Input() hasHistoryContent: boolean = false;

  /**
   * View mode flag - adapts UI for view-only display
   */
  @Input() isViewMode: boolean = false;

  /**
   * Show time elapsed tab
   */
  @Input() showTimeElapsed: boolean = false;

  /**
   * Lock state - disables all actions when true
   */
  @Input() isLocked: boolean = false;

  /**
   * Hide Details, Comments, and History tabs (for create mode)
   */
  @Input() hideDetailsCommentsHistory: boolean = false;

  /**
   * Emits when active tab changes
   */
  @Output() tabChange = new EventEmitter<TaskTabChangeEvent>();

  /**
   * Emits when description edit button is clicked
   */
  @Output() editDescription = new EventEmitter<void>();

  /** View screen only: the edited description; the parent saves it and calls done(true|false). */
  @Output() descriptionSave = new EventEmitter<{ value: string; done: (saved: boolean) => void }>();

  public isEditingDescription = false;
  public isSavingDescription = false;
  private descriptionBeforeEdit = '';

  /**
   * Emits when milestone remove button is clicked
   */
  @Output() removeMilestone = new EventEmitter<void>();

  /**
   * Module & Reference Selection Outputs
   */
  @Output() moduleSelected = new EventEmitter<any>();
  @Output() itemSelected = new EventEmitter<any>();
  @Output() itemCleared = new EventEmitter<void>();

  /**
   * Mention feature outputs
   */
  @Output() mentionSelected = new EventEmitter<any>();
  @Output() mentionSearch = new EventEmitter<string>();

  /**
   * Task name form control
   */
  public taskNameControl: FormControl;

  /**
   * Task description form control
   */
  public taskDescriptionControl: FormControl;

  /**
   * Active tab index (internal state)
   */
  public activeTabIndex: number = 0;

  /**
   * Description target div reference
   */
  @ViewChild('descriptionTarget') descriptionTarget!: ElementRef;

  /**
   * Custom mention dropdown state
   */
  public showMentionDropdown: boolean = false;
  public mentionDropdownTop: number = 0;
  public mentionDropdownLeft: number = 0;
  public selectedMentionIndex: number = 0;
  private currentMentionSearch: string = '';
  private mentionStartPos: number = 0;
  private clickOutsideListener?: (event: MouseEvent) => void;

  constructor(private cdr: ChangeDetectorRef) {
    // Initialize form controls with validators
    this.taskNameControl = new FormControl('', [Validators.required]);
    this.taskDescriptionControl = new FormControl('');
  }

  /**
   * Angular lifecycle hook - component initialization
   */
  ngOnInit(): void {
    this.validateInputs();
    this.syncFormControls();
    this.activeTabIndex = this.activeTab || 0;

    // Prevent cursor from going inside mention chips
    this.setupMentionChipProtection();
  }

  /**
   * Angular lifecycle hook - component destruction
   */
  ngOnDestroy(): void {
    this.removeClickOutsideListener();
  }

  /**
   * Angular lifecycle hook - detect input changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['taskForm']) {
      this.validateInputs();
      this.syncFormControls();
    }

    if (changes['activeTab']) {
      this.activeTabIndex = this.activeTab || 0;
    }
  }

  /**
   * Validate required inputs are provided
   * @private
   */
  private validateInputs(): void {
    if (!this.taskForm) {
      console.warn('TaskMainContentComponent: taskForm input is required');
    }
  }

  /**
   * Sync form controls with taskForm
   * @private
   */
  private syncFormControls(): void {
    if (!this.taskForm) {
      return;
    }

    // Sync task name
    const titleControl = this.taskForm.get('title');
    if (titleControl) {
      this.taskNameControl.setValue(titleControl.value, { emitEvent: false });

      // Subscribe to changes
      this.taskNameControl.valueChanges.subscribe((value) => {
        titleControl.setValue(value, { emitEvent: false });
      });

      titleControl.valueChanges.subscribe((value) => {
        this.taskNameControl.setValue(value, { emitEvent: false });
      });
    }

    // Sync description
    const descriptionControl = this.taskForm.get('description');
    if (descriptionControl) {
      this.taskDescriptionControl.setValue(descriptionControl.value, {
        emitEvent: false,
      });

      // Render initial description content to DOM
      if (descriptionControl.value && this.descriptionTarget) {
        this.descriptionTarget.nativeElement.innerHTML =
          descriptionControl.value;
      }

      // Subscribe to changes
      this.taskDescriptionControl.valueChanges.subscribe((value) => {
        descriptionControl.setValue(value, { emitEvent: false });
      });

      descriptionControl.valueChanges.subscribe((value) => {
        this.taskDescriptionControl.setValue(value, { emitEvent: false });
        // Update DOM when form control changes
        if (this.descriptionTarget) {
          this.descriptionTarget.nativeElement.innerHTML = value || '';
        }
      });
    }
  }

  /**
   * Handle tab change
   * @param index New tab index
   */
  public onTabChange(index: number): void {
    const event: TaskTabChangeEvent = {
      previousIndex: this.activeTabIndex,
      currentIndex: index,
    };

    this.activeTabIndex = index;
    this.tabChange.emit(event);
  }

  /**
   * Handle description edit button click
   */
  public onEditDescription(): void {
    this.editDescription.emit();
    // View screen: the pencil turns the read-only box into an editor with Save / Cancel
    if (this.isViewMode && !this.isLocked) {
      this.descriptionBeforeEdit = this.descriptionTarget?.nativeElement.innerHTML ?? '';
      this.isEditingDescription = true;
      setTimeout(() => this.descriptionTarget?.nativeElement.focus());
    }
  }

  /** Cancel the inline edit and restore the text as it was. */
  public cancelDescriptionEdit(): void {
    if (this.descriptionTarget) {
      this.descriptionTarget.nativeElement.innerHTML = this.descriptionBeforeEdit;
    }
    this.isEditingDescription = false;
  }

  /** Hand the edited text to the parent, which saves it; the editor closes once the save succeeds. */
  public saveDescriptionEdit(): void {
    const text = this.extractPlainText(this.descriptionTarget?.nativeElement.innerHTML ?? '').trim();
    this.isSavingDescription = true;
    this.descriptionSave.emit({
      value: text,
      done: (saved: boolean) => {
        this.isSavingDescription = false;
        if (saved) {
          this.isEditingDescription = false;
        }
      },
    });
  }

  /**
   * Handle milestone remove button click
   */
  public onRemoveMilestone(): void {
    this.removeMilestone.emit();
  }

  /**
   * Get milestone status CSS class
   * @returns CSS class for milestone status
   */
  public getMilestoneStatusClass(): string {
    if (!this.selectedMilestone) {
      return '';
    }

    const statusMap: Record<string, string> = {
      upcoming: 'milestone-upcoming',
      'in-progress': 'milestone-in-progress',
      completed: 'milestone-completed',
      overdue: 'milestone-overdue',
    };

    return statusMap[this.selectedMilestone.status] || 'milestone-upcoming';
  }

  /**
   * Format milestone due date
   * @param date Date to format
   * @returns Formatted date string
   */
  public formatMilestoneDate(date: Date | string | undefined): string {
    if (!date) {
      return 'No due date';
    }

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    // Format as "MMM DD, YYYY"
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };

    return dateObj.toLocaleDateString('en-US', options);
  }

  /**
   * Handle module selection
   * @param event Module selection event
   */
  public onModuleSelected(event: any): void {
    this.moduleSelected.emit(event);
  }

  /**
   * Handle reference item selection
   * @param event Selected item
   */
  public onItemSelected(event: any): void {
    this.itemSelected.emit(event);
  }

  /**
   * Handle clearing selected item
   */
  public onItemCleared(): void {
    this.itemCleared.emit();
  }

  /**
   * Handle keyup events in description for custom @ mention detection
   * @param event Keyboard event
   */
  public onDescriptionKeyup(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    const text = target.textContent || '';
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) return;

    const cursorPos = this.getCaretPosition(target);

    // Check for @ symbol
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);

      // Check if there's a space after @, if so, close dropdown
      if (textAfterAt.includes(' ')) {
        this.closeMentionDropdown();
        return;
      }

      // Show dropdown with search query
      this.currentMentionSearch = textAfterAt;
      this.mentionStartPos = lastAtIndex;

      // Calculate dropdown position
      const rect = this.getCaretCoordinates();
      this.mentionDropdownTop = rect.top;
      this.mentionDropdownLeft = rect.left;

      this.showMentionDropdown = true;
      this.selectedMentionIndex = 0;

      // Setup click outside listener
      this.setupClickOutsideListener();

      // Trigger search
      this.mentionSearch.emit(textAfterAt);
    } else {
      this.closeMentionDropdown();
    }

    // Handle arrow keys and Enter in dropdown
    if (this.showMentionDropdown) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.selectedMentionIndex = Math.min(this.selectedMentionIndex + 1, this.mentions.length - 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.selectedMentionIndex = Math.max(this.selectedMentionIndex - 1, 0);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.mentions[this.selectedMentionIndex]) {
          this.selectMention(this.mentions[this.selectedMentionIndex]);
        }
      } else if (event.key === 'Escape') {
        this.closeMentionDropdown();
      }
    }
  }

  /**
   * Get caret position in contenteditable div
   */
  private getCaretPosition(element: HTMLElement): number {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return 0;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    return preCaretRange.toString().length;
  }

  /**
   * Get caret coordinates for dropdown positioning (viewport coordinates)
   */
  private getCaretCoordinates(): {top: number, left: number, height: number} {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return { top: 0, left: 0, height: 20 };
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();

    // Base viewport coordinates (for fixed positioning)
    let top = rect.bottom + window.scrollY; // Position below cursor
    let left = rect.left + window.scrollX;

    // -----------------------------------------------------------------------
    // Clamp dropdown horizontally so it never overflows off the right edge
    // -----------------------------------------------------------------------
    // NOTE:
    // - The dropdown has a fixed max-width in CSS (around 280px). We add
    //   some padding to make sure it stays visually inside the viewport.
    // - This keeps the behaviour identical for most cases but when the
    //   cursor is near the right side we shift the dropdown to the left.
    const DROPDOWN_WIDTH = 280; // px – must roughly match .custom-mention-dropdown width
    const PADDING = 16;         // px – distance from viewport edge
    const viewportWidth = window.innerWidth;

    const maxLeft = viewportWidth - DROPDOWN_WIDTH - PADDING;
    if (left > maxLeft) {
      left = Math.max(PADDING, maxLeft);
    }

    return {
      top,
      left,
      height: rect.height || 20
    };
  }

  /**
   * Select a mention from dropdown
   * IMPROVED: Simple and reliable - preserves all existing mention chips
   */
  public selectMention(user: any): void {
    const target = this.descriptionTarget.nativeElement;
    const currentHTML = target.innerHTML;

    // Create the new mention chip HTML
    const mentionText = `@${user.full_name}`;
    const searchText = `@${this.currentMentionSearch}`;
    const mentionHTML = `<span class="e-mention-chip" data-mention-value="${user.id}" contenteditable="false">${mentionText}</span>`;

    // Simple approach: Find the search text in HTML and replace it
    // This preserves all existing chips because we're doing a targeted replacement

    // Get the position in the plain text
    const plainText = target.textContent || '';
    const searchStartPos = this.mentionStartPos;
    const searchEndPos = searchStartPos + searchText.length;

    // Build the replacement by working with innerHTML segments
    let newHTML = currentHTML;
    let replacementDone = false;

    // Method 1: Try direct replacement if search text exists in HTML
    if (currentHTML.includes(searchText)) {
      // Find the last occurrence (most recent typed)
      const lastIndex = currentHTML.lastIndexOf(searchText);

      // Make sure we're not replacing inside a chip
      const beforeSearchHTML = currentHTML.substring(0, lastIndex);
      const afterSearchHTML = currentHTML.substring(lastIndex + searchText.length);

      // Check if we're inside a chip by looking for unclosed chip tags
      const openChipCount = (beforeSearchHTML.match(/<span[^>]*class="[^"]*e-mention-chip[^"]*"/g) || []).length;
      const closeChipCount = (beforeSearchHTML.match(/<\/span>/g) || []).length;

      if (openChipCount === closeChipCount) {
        // We're not inside a chip, safe to replace
        newHTML = beforeSearchHTML + mentionHTML + '&nbsp;' + afterSearchHTML;
        replacementDone = true;
      }
    }

    // Method 2: If direct replacement didn't work, walk through the DOM
    if (!replacementDone) {
      newHTML = this.replaceSearchTextInHTML(currentHTML, searchText, mentionHTML, this.mentionStartPos);
    }

    // Set the new HTML
    target.innerHTML = newHTML;

    // Place cursor after the new mention
    setTimeout(() => {
      // Find the newly inserted chip and place cursor after it
      const allChips = target.querySelectorAll('.e-mention-chip');
      if (allChips.length > 0) {
        const lastChip = allChips[allChips.length - 1] as HTMLElement;
        this.moveCursorAfterElement(lastChip);
      }
    }, 10);

    // Extract and emit ALL mentioned users
    setTimeout(() => {
      this.extractAndEmitMentionedUsers(newHTML);

      // Update form control with plain text
      const newPlainText = this.extractPlainText(newHTML);
      this.taskDescriptionControl.setValue(newPlainText, { emitEvent: false });

      const descriptionControl = this.taskForm?.get('description');
      if (descriptionControl) {
        descriptionControl.setValue(newPlainText, { emitEvent: false });
      }
    }, 50);

    // Close dropdown
    this.closeMentionDropdown();
  }

  /**
   * Replace search text in HTML while preserving existing chips
   * Walks through the HTML character by character
   */
  private replaceSearchTextInHTML(html: string, searchText: string, replacement: string, targetPosition: number): string {
    // Parse HTML to work with DOM
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Walk through text nodes to find the target position
    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT);
    let currentPos = 0;
    let targetNode: Text | null = null;
    let positionInNode = 0;

    let node;
    while (node = walker.nextNode()) {
      const textNode = node as Text;
      const text = textNode.textContent || '';
      const len = text.length;

      if (currentPos <= targetPosition && targetPosition < currentPos + len) {
        // Found the node containing our target position
        targetNode = textNode;
        positionInNode = targetPosition - currentPos;
        break;
      }

      currentPos += len;
    }

    if (targetNode) {
      const text = targetNode.textContent || '';
      const beforeText = text.substring(0, positionInNode);
      const afterText = text.substring(positionInNode + searchText.length);

      // Create replacement nodes
      const fragment = document.createDocumentFragment();

      if (beforeText) {
        fragment.appendChild(document.createTextNode(beforeText));
      }

      // Parse the replacement HTML
      const tempSpan = document.createElement('span');
      tempSpan.innerHTML = replacement + '&nbsp;';
      while (tempSpan.firstChild) {
        fragment.appendChild(tempSpan.firstChild);
      }

      if (afterText) {
        fragment.appendChild(document.createTextNode(afterText));
      }

      // Replace the text node
      targetNode.parentNode?.replaceChild(fragment, targetNode);
    }

    return tempDiv.innerHTML;
  }


  /**
   * Place cursor after the last mention chip
   */
  private placeCursorAfterLastMention(element: HTMLElement): void {
    const chips = element.querySelectorAll('.e-mention-chip');
    if (chips.length > 0) {
      const lastChip = chips[chips.length - 1] as HTMLElement;
      this.moveCursorAfterElement(lastChip);
    } else {
      // If no chips, place at end
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(element);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }

  /**
   * Setup click outside listener to close dropdown
   */
  private setupClickOutsideListener(): void {
    // Remove existing listener if any
    this.removeClickOutsideListener();

    // Add new listener after a small delay to avoid immediate closure
    setTimeout(() => {
      this.clickOutsideListener = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const dropdownEl = document.querySelector('.custom-mention-dropdown');
        const descriptionEl = this.descriptionTarget?.nativeElement;

        // Check if click is outside both dropdown and description field
        if (dropdownEl && !dropdownEl.contains(target) &&
            descriptionEl && !descriptionEl.contains(target)) {
          this.closeMentionDropdown();
          this.cdr.detectChanges();
        }
      };

      document.addEventListener('click', this.clickOutsideListener as any);
    }, 100);
  }

  /**
   * Remove click outside listener
   */
  private removeClickOutsideListener(): void {
    if (this.clickOutsideListener) {
      document.removeEventListener('click', this.clickOutsideListener as any);
      this.clickOutsideListener = undefined;
    }
  }

  /**
   * Close mention dropdown
   */
  private closeMentionDropdown(): void {
    this.showMentionDropdown = false;
    this.currentMentionSearch = '';
    this.selectedMentionIndex = 0;
    this.removeClickOutsideListener();
  }

  /**
   * Set cursor position in contenteditable
   */
  private setCursorPosition(element: HTMLElement, position: number): void {
    const range = document.createRange();
    const selection = window.getSelection();

    let currentPos = 0;
    let found = false;

    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const length = node.textContent?.length || 0;

      if (currentPos + length >= position) {
        range.setStart(node, position - currentPos);
        range.collapse(true);
        found = true;
        break;
      }

      currentPos += length;
    }

    if (!found) {
      range.selectNodeContents(element);
      range.collapse(false);
    }

    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  /**
   * Escape HTML for safe insertion
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Setup protection to prevent cursor from entering mention chips
   * Moves cursor outside chip when user tries to edit inside it
   */
  private setupMentionChipProtection(): void {
    setTimeout(() => {
      if (!this.descriptionTarget) return;

      const target = this.descriptionTarget.nativeElement;

      // Handle click events on chips
      target.addEventListener('click', (event: MouseEvent) => {
        const clickedElement = event.target as HTMLElement;

        // Check if clicked on a mention chip
        if (clickedElement.classList.contains('e-mention-chip') ||
            clickedElement.closest('.e-mention-chip')) {
          const chip = clickedElement.classList.contains('e-mention-chip')
            ? clickedElement
            : clickedElement.closest('.e-mention-chip');

          if (chip) {
            // Move cursor after the chip
            this.moveCursorAfterElement(chip as HTMLElement);
            event.preventDefault();
            event.stopPropagation();
          }
        }
      });

      // Handle keydown to prevent editing inside chips
      target.addEventListener('keydown', (event: KeyboardEvent) => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        let node = range.startContainer;

        // Check if cursor is inside a chip
        while (node && node !== target) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            if (element.classList.contains('e-mention-chip')) {
              // Cursor is inside chip, move it outside
              if (event.key === 'Backspace' || event.key === 'Delete') {
                // Allow deletion of entire chip
                element.remove();
                event.preventDefault();
                return;
              } else if (event.key === 'ArrowLeft') {
                this.moveCursorBeforeElement(element);
                event.preventDefault();
                return;
              } else if (event.key === 'ArrowRight') {
                this.moveCursorAfterElement(element);
                event.preventDefault();
                return;
              } else {
                // For other keys, move cursor after chip
                this.moveCursorAfterElement(element);
                return; // Let the key be processed after cursor move
              }
            }
          }
          node = node.parentNode as Node;
        }
      });
    }, 100);
  }

  /**
   * Move cursor before an element
   */
  private moveCursorBeforeElement(element: HTMLElement): void {
    const range = document.createRange();
    const selection = window.getSelection();

    range.setStartBefore(element);
    range.collapse(true);

    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  /**
   * Move cursor after an element
   */
  private moveCursorAfterElement(element: HTMLElement): void {
    const range = document.createRange();
    const selection = window.getSelection();

    range.setStartAfter(element);
    range.collapse(true);

    selection?.removeAllRanges();
    selection?.addRange(range);

    // Insert a zero-width space to ensure cursor has somewhere to go
    const textNode = document.createTextNode('\u200B');
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);

    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  /**
   * Manually refresh mention dropdown (called by parent after API updates)
   */
  public refreshMentionDropdown(): void {
    // Force change detection to update the custom dropdown
    this.cdr.detectChanges();
    console.log('[CHILD] Custom dropdown refreshed with', this._mentions.length, 'users');
  }

  /**
   * Handle description content change
   * @param event Blur event from contenteditable div
   */
  public onDescriptionChange(event: any): void {
    const htmlContent = event.target.innerHTML;

    // Extract plain text for FormControl
    const plainText = this.extractPlainText(htmlContent);
    this.taskDescriptionControl.setValue(plainText, { emitEvent: false });

    // Manually sync to parent form control to ensure save captures current value
    const descriptionControl = this.taskForm?.get('description');
    if (descriptionControl) {
      descriptionControl.setValue(plainText, { emitEvent: false });
    }

    // Extract mentioned user IDs
    this.extractMentionedUsers(htmlContent);
  }

  /**
   * Handle description input (real-time updates)
   * @param event Input event from contenteditable div
   */
  public onDescriptionInput(event: any): void {
    const htmlContent = event.target.innerHTML;
    const plainText = this.extractPlainText(htmlContent);
    this.taskDescriptionControl.setValue(plainText, { emitEvent: false });

    // Manually sync to parent form control to ensure save captures current value
    const descriptionControl = this.taskForm?.get('description');
    if (descriptionControl) {
      descriptionControl.setValue(plainText, { emitEvent: false });
    }

    // Extract and emit all mentioned users whenever content changes
    this.extractAndEmitMentionedUsers(htmlContent);
  }

  /**
   * Extract plain text from HTML content
   * @param html HTML content string
   * @returns Plain text string
   */
  private extractPlainText(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  /**
   * Extract mentioned user IDs from HTML content
   * @param html HTML content with mention spans
   */
  private extractMentionedUsers(html: string): void {
    this.extractAndEmitMentionedUsers(html);
  }

  /**
   * Extract and emit ALL mentioned users from HTML content
   * This collects all mention chips and sends them to parent
   * @param html HTML content with mention spans
   */
  private extractAndEmitMentionedUsers(html: string): void {
    // Parse HTML to find all mention chips
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const mentionChips = doc.querySelectorAll('.e-mention-chip');

    const mentionedUsers: any[] = [];
    const uniqueUserIds = new Set<number>();

    mentionChips.forEach((chip) => {
      const userId = chip.getAttribute('data-mention-value');
      const userName = chip.textContent?.replace('@', '').trim();

      if (userId) {
        const userIdNum = parseInt(userId, 10);
        // Only add if not already in set (avoid duplicates)
        if (!uniqueUserIds.has(userIdNum)) {
          uniqueUserIds.add(userIdNum);
          mentionedUsers.push({
            id: userIdNum,
            name: userName || '',
            full_name: userName || ''
          });
        }
      }
    });

    console.log('[CUSTOM MENTION] Extracted mentioned users:', mentionedUsers);

    // Always emit the full array of mentioned users (even if empty)
    this.mentionSelected.emit(mentionedUsers);
  }

  /**
   * Set description content with mentions (for edit mode)
   * @param text Plain text description
   * @param mentionedUsers Array of mentioned users
   */
  public setDescriptionContent(text: string, mentionedUsers: any[] = []): void {
    if (!this.descriptionTarget) return;

    let html = text;

    // Replace plain @mentions with styled spans
    mentionedUsers.forEach((user) => {
      const fullName = user.full_name || user.name;
      const mentionText = `@${fullName}`;
      const mentionHtml = `<span class="e-mention-chip" data-mention-value="${user.id}" contenteditable="false">${mentionText}</span>`;
      html = html.replace(new RegExp(mentionText, 'g'), mentionHtml);
    });

    this.descriptionTarget.nativeElement.innerHTML = html;
  }

  /**
   * Get checklist tab index based on create/edit mode
   * @returns Tab index for checklist tab
   */
  public getChecklistTabIndex(): number {
    return this.hideDetailsCommentsHistory ? 0 : 1;
  }

  /**
   * Get attachments tab index based on create/edit mode
   * @returns Tab index for attachments tab
   */
  public getAttachmentsTabIndex(): number {
    return this.hideDetailsCommentsHistory ? 1 : 2;
  }
}
